import React, { useRef, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";

// Import date-fns locales
import { enUS, mk } from "date-fns/locale";

// Register locales for DatePicker
registerLocale("en", enUS);
registerLocale("mk", mk);
const DATE_PICKER_OPEN_EVENT = "erp:date-picker-open";

interface DateCurrentInputProps {
  value?: string; // Expected format: DD.MM.YYYY
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  inputRef?: (el: HTMLInputElement | null) => void;
  inputClassName?: string;
  onBlur?: () => void;
  showIcon?: boolean;
  placeholder?: string;
  clearable?: boolean; // false = no × clear button (e.g. compact table cells); default true
  openToDate?: string; // DD.MM.YYYY — month the calendar opens on while the field is empty (e.g. the plan year)
  // false = typed only, no calendar popup; onChange fires once, on Enter or leaving the field
  // (a cell that saves at once must not save every half-typed date). Default true.
  calendar?: boolean;
}

const DateCurrentInput: React.FC<DateCurrentInputProps> = ({ value, onChange, required, disabled = false, onKeyDown, inputRef, inputClassName, onBlur, showIcon = false, placeholder, clearable = true, openToDate, calendar = true }) => {
  const { i18n } = useTranslation();
  const datePickerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  // calendar=false: the typed date waits here until Enter / blur.
  const pendingRef = useRef<string | null>(null);
  const commitPending = () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    // A half-typed year („26.12.202“) parses as year 202 — never save that.
    const year = pending ? moment(pending, "DD.MM.YYYY", true).year() : null;
    if (year !== null && year < 1900) return;
    if (pending !== null && pending !== (value || "")) onChange(pending);
  };

  // Get current language for locale
  const currentLocale = i18n.language === 'mk' ? 'mk' : 'en';

  // If value exists and is valid, parse it, otherwise use null (no default date)
  const parsedMoment = value ? moment(value, "DD.MM.YYYY", true) : null;
  const selectedDate = parsedMoment && parsedMoment.isValid()
    ? parsedMoment.toDate()
    : null;

  const handleChange = (date: Date | null) => {
    if (!calendar) {
      pendingRef.current = date ? moment(date).format("DD.MM.YYYY") : "";
      return;
    }
    if (date) {
      onChange(moment(date).format("DD.MM.YYYY"));
    } else {
      onChange("");
    }
  };

  // Handle keydown on the input element
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!calendar && e.key === "Enter") commitPending();
    // Keep arrow/enter navigation inside datepicker when popup is open.
    if (
      isCalendarOpen &&
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter"].includes(e.key)
    ) {
      e.stopPropagation();
      return;
    }

    if (onKeyDown) {
      onKeyDown(e as any);
    }
  };

  const openCalendar = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(DATE_PICKER_OPEN_EVENT, {
          detail: { source: containerRef.current },
        })
      );
    }
    setIsCalendarOpen(true);
  };

  const handleBlur = () => {
    if (!calendar) commitPending();
    setIsCalendarOpen(false);
    if (onBlur) onBlur();
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleOtherDatePickerOpen = (event: Event) => {
      const customEvent = event as CustomEvent<{ source: HTMLDivElement | null }>;
      if (customEvent.detail?.source !== containerRef.current) {
        setIsCalendarOpen(false);
      }
    };

    window.addEventListener(DATE_PICKER_OPEN_EVENT, handleOtherDatePickerOpen);
    return () => {
      window.removeEventListener(DATE_PICKER_OPEN_EVENT, handleOtherDatePickerOpen);
    };
  }, []);

  // Forward ref to the actual input element inside DatePicker
  useEffect(() => {
    if (inputRef && containerRef.current) {
      // Find the input element inside the DatePicker wrapper
      const inputElement = containerRef.current.querySelector('input') as HTMLInputElement | null;
      if (inputElement) {
        inputRef(inputElement);
      }
    }
  }, [inputRef]);

  return (
    <div ref={containerRef} className="flex flex-col w-full">
      <DatePicker
        ref={datePickerRef}
        selected={selectedDate}
        openToDate={!selectedDate && openToDate && moment(openToDate, "DD.MM.YYYY", true).isValid() ? moment(openToDate, "DD.MM.YYYY").toDate() : undefined}
        open={calendar && isCalendarOpen}
        onChange={handleChange}
        onInputClick={calendar ? openCalendar : undefined}
        onCalendarOpen={calendar ? openCalendar : undefined}
        onCalendarClose={() => setIsCalendarOpen(false)}
        dateFormat="dd.MM.yyyy"
        placeholderText={placeholder ?? "DD.MM.YYYY"}
        required={required}
        disabled={disabled}
        onBlur={handleBlur}
        showIcon={showIcon}
        toggleCalendarOnIconClick={showIcon}
        wrapperClassName="w-full"
        className={
          inputClassName ??
          `w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            disabled ? 'bg-gray-50 cursor-not-allowed' : ''
          }`
        }
        showPopperArrow={false}
        isClearable={clearable && !disabled}
        locale={currentLocale}
        popperPlacement="bottom-start"
        popperClassName="z-[99999]"
        popperProps={{ strategy: "fixed" }}
        portalId="date-picker-portal"
        onKeyDown={handleKeyDown as any}
      />
    </div>
  );
};

export default DateCurrentInput;